import { computed } from 'mobx'
import { task } from 'mobx-task'
import { switchMap } from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { type Job } from '@/features/jobs/Job'
import type { RequestedSupportingItem } from '@/features/jobs/info-gathering/RequestedSupportingItem'
import type { BusinessInfoSupportingItemInput } from './BusinessInfoSupportingItemInput'
import {
  formatProfileDate,
  maskEmployerIdentificationNumber,
} from '@/features/jobs/info-gathering/info-gathering-formatters'
import { type BusinessProfileStore } from './BusinessProfileStore'

/**
 * The business profile the client provided for a job.
 */
export class BusinessInfoViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Loads the business profile.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._fetch(signal)
  })

  constructor(
    private readonly _job: Job,
    readonly item: RequestedSupportingItem<BusinessInfoSupportingItemInput>,
    private readonly _businessProfileStore: BusinessProfileStore,
  ) {
    // Re-fetches the profile when another one is provided.
    this._lifecycle.manage(
      toStream(() => this.item.input?.provided).pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            // A failed refresh keeps showing the previously loaded value.
            this._fetch(signal).catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * The provided business profile version.
   */
  @computed get profile() {
    const provided = this.item.input?.provided
    if (!provided?.profileId || !provided.profileVersion) {
      return null
    }

    return this._businessProfileStore.getBusinessProfile(
      provided.profileId,
      provided.profileVersion,
    )
  }

  /**
   * Whether the provided profile has been fetched; editing before then would
   * start from a blank profile and overwrite it.
   */
  @computed get isLoaded() {
    return !this.item.completed || !!this.profile
  }

  /**
   * The business's formatted date of incorporation.
   */
  @computed get dateOfIncorporation() {
    return formatProfileDate(
      this.profile?.businessInfo.dateOfIncorporation ?? null,
    )
  }

  /**
   * The business's masked employer identification number.
   */
  @computed get maskedEmployerIdentificationNumber() {
    return maskEmployerIdentificationNumber(
      this.profile?.businessInfo.employerIdentificationNumber ?? '',
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The section renders a failure from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _fetch(signal: AbortSignal) {
    if (this.item.completed) {
      await this._businessProfileStore.fetchBusinessProfileForJob(
        this._job.id,
        signal,
      )
    }
  }
}
